import { SharedModule } from '@/app/shared/shared.module';
import { Component, OnInit, OnDestroy, EventEmitter, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { UserService } from '@/app/core/services/user.service';
import { TutorialService } from '@/app/core/services/tutorial.service';
import { LocalStorageService } from '@/app/core/storage/local-storage.service';
import { Subscription } from 'rxjs';
import { Popover } from 'primeng/popover';
import { PermissionService } from '@/app/core/services/permission.service';
import { PlantationService } from '@/app/core/services/plantation.service';
import { NotificationService } from '@/app/core/services/notification.service';

enum IconState {
  Filled = 'ph-fill',
  Regular = 'ph',
}

export interface NavigationMenuItem {
  key: string;
  label: string;
  iconState: string;
  icon: string;
  iconFill: string;
  routerLink: string,
  visible: boolean
}

@Component({
  selector: 'app-navigation-bar',
  imports: [SharedModule],
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.less',
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  isCSIDomain = window.location.hostname.includes('sustainability.doktar');
  isDoktarAppDomain = window.location.hostname.includes('doktar.app');
  isStaging = window.location.hostname.includes('staging');
  isLocalHost = window.location.hostname.includes('localhost');
  menuItems: NavigationMenuItem[] = [
    {
      key: 'home',
      label: 'doktar_app.menu.home',
      iconState: 'ph-fill',
      icon: 'ph-fill ph-house',
      iconFill: '',
      routerLink: '/home',
      visible: false,
    },
    {
      key: 'services',
      label: 'doktar_app.menu.services',
      iconState: '',
      icon: 'ph-fill ph-diamonds-four',
      iconFill: '',
      routerLink: '/services',
      visible: this.isStaging || !this.isCSIDomain,
    },
    {
      key: 'land_and_plants',
      label: 'doktar_app.menu.land_and_plants',
      iconState: '',
      icon: 'ph-fill ph-columns-plus-right',
      iconFill: '',
      routerLink: 'lands',
      visible: false,
    },
    {
      key: 'csi',
      // label: 'doktar_app.menu.csi',
      label: 'CSI',
      iconState: '',
      icon: 'ph-fill ph-suitcase',
      iconFill: '',
      routerLink: '/csi',
      visible: false,
    },
    {
      key: 'sustainability-dashboard-static',
      // label: 'doktar_app.menu.sustainability-dashboard',
      label: 'Sustainability Dashboard',
      iconState: '',
      icon: 'ph-fill ph-globe-hemisphere-west',
      iconFill: '',
      routerLink: '/sustainability-dashboard-s',
      visible: false
    },
    {
      key: 'sustainability-dashboard',
      label: 'Sustainability Dashboard',
      iconState: '',
      icon: 'ph-fill ph-chart-pie',
      iconFill: '',
      routerLink: '/sustainability-dashboard-d',
      visible: false
    },
    {
      key: 'activities',
      // label: 'doktar_app.menu.csi',
      label: 'doktar_app.common.activities',
      iconState: '',
      icon: 'ph-fill ph-list-checks',
      iconFill: '',
      routerLink: '/activities',
      visible: this.isStaging || !this.isCSIDomain,
    },
  ];

  subMenuItems: NavigationMenuItem[] = [
    {
      key: 'support',
      label: 'doktar_app.menu.support',
      iconState: 'ph-fill',
      icon: 'ph-fill ph-headset',
      iconFill: '',
      routerLink: '/support',
      visible: this.isStaging || !this.isCSIDomain,
    },
    {
      key: 'support-ticket',
      label: 'doktar_app.menu.support_ticket',
      iconState: 'ph-fill',
      icon: 'ph-fill ph-chat-circle',
      iconFill: '',
      routerLink: '/support-ticket',
      visible: this.isStaging || !this.isCSIDomain,
    },
    {
      key: 'notifications',
      label: 'doktar_app.menu.notifications',
      iconState: 'ph-fill',
      icon: 'ph-fill ph-bell-simple',
      iconFill: '',
      routerLink: '',
      visible: this.isStaging || !this.isCSIDomain,
    },
  ];

  profileLabelLetters: string = '';
  private userSubscription: Subscription;
  private tutorialSubscription: Subscription;
  isLandsPopoverVisible: boolean = false;

  activeMenu: NavigationMenuItem;
  @Output() changeActiveMenu = new EventEmitter<NavigationMenuItem>();
  @Output() toggleNotifications = new EventEmitter<boolean>();
  @ViewChild('landsPopover') landsPopover!: Popover;

  constructor(
    private _router: Router,
    private _authService: AuthService,
    private _userService: UserService,
    private _tutorialService: TutorialService,
    private _localStorageService: LocalStorageService,
    private _permissionService: PermissionService,
    private _plantationService: PlantationService,
    private _notificationService: NotificationService
  ) {
    // Subscribe to user data changes
    this.userSubscription = this._userService.currentUser$.subscribe(() => {
      this.profileLabelLetters = this._userService.getUserInitials();
    });

    // Subscribe to tutorial service changes
    this.tutorialSubscription = this._tutorialService.isLandsPopoverVisible$.subscribe((isVisible) => {
      this.isLandsPopoverVisible = isVisible;
      if (isVisible) {
        setTimeout(() => {
          const landsElement = document.getElementById('item-lands');
          if (this.landsPopover && landsElement) {
            const mockEvent = {
              currentTarget: landsElement,
              target: landsElement,
              preventDefault: () => {},
              stopPropagation: () => {},
            } as unknown as Event;
            this.landsPopover.show(mockEvent, landsElement);
          }
        });
      }
    });

    this._permissionService.permission$.subscribe((res) => {
      if (res) {
        const isCSIAuthorized = this._permissionService.isGrantedOnUserPermissions('CSIContract_Get');
        if (isCSIAuthorized) {
          const csiMenuIndex = this.menuItems.findIndex((e) => e.key === 'csi');
          const isDomainValid = this.isCSIDomain || this.isLocalHost || this.isStaging;
          this.menuItems[csiMenuIndex].visible = isDomainValid;
        }

        const isSustainabilityAuthorized = this._permissionService.isGrantedOnUserPermissions('Sustainability_Get');
        if (isSustainabilityAuthorized) {
          const isDomainValid = this.isCSIDomain || this.isLocalHost || this.isStaging;
          const sustainabilityDashboardStaticIndex = this.menuItems.findIndex(e => e.key === 'sustainability-dashboard-static');
          const sustainabilityDashboardIndex = this.menuItems.findIndex(e => e.key === 'sustainability-dashboard');
          if (sustainabilityDashboardStaticIndex !== -1) {
            this.menuItems[sustainabilityDashboardStaticIndex].visible = isDomainValid;
          }
          if (sustainabilityDashboardIndex !== -1) {
            this.menuItems[sustainabilityDashboardIndex].visible = isDomainValid && !this.isDoktarAppDomain;
          }
        }

        const landAndPlantsIndex = this.menuItems.findIndex((e) => e.key === 'land_and_plants');
        const isLandAuthorized = this._permissionService.isGrantedOnUserPermissions('Land_Get');
        const isPlantationAuthorized = this._permissionService.isGrantedOnUserPermissions('Plantations_Get');
        this.menuItems[landAndPlantsIndex].visible = !this.isCSIDomain && isLandAuthorized && isPlantationAuthorized;

        const homeIndex = this.menuItems.findIndex((e) => e.key === 'home');
        const isHomeAuthorized = this._permissionService.isGrantedOnUserPermissions('HomePage_Get');
        this.menuItems[homeIndex].visible = !this.isCSIDomain && isHomeAuthorized;
      }
    });
  }

  ngOnInit(): void {
    this._userService.initializeUserService().then();

    this.profileLabelLetters = this._userService.getUserInitials();

    const activeMenu = this.menuItems.find((item) => item.routerLink === this._router.url);
    if (activeMenu) {
      this.activeMenu = activeMenu;
      this.changeActiveMenu.emit(this.activeMenu);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.tutorialSubscription) {
      this.tutorialSubscription.unsubscribe();
    }
  }

  onClickMenuItem(menuItem: NavigationMenuItem): void {
    this.toggleNotifications.emit(false);
    if (menuItem.key !== 'services') {
      this._plantationService.showSelectPlantationPanel(false);
    } else {
      this._plantationService.showSelectPlantationPanel(true);
    }

    // Prevent navigation when lands popover is visible unless it's the lands menu
    if (this.isLandsPopoverVisible && menuItem.routerLink === 'lands') {
      return;
    }

    this.activeMenu = menuItem;
    this.changeActiveMenu.emit(this.activeMenu);
    this._router.navigateByUrl(menuItem.routerLink);
  }

  onNotificationsClick(): void {
    // Prevent notifications when lands popover is visible
    if (this.isLandsPopoverVisible) {
      return;
    }
    this.toggleNotifications.emit(true);
  }

  getUserInformation(): { name: string; surname: string } {
    const user = this._userService.currentUser;
    return {
      name: user?.name ?? '',
      surname: user?.surname ?? '',
    };
  }

  getProfileInitials(): string {
    return this._userService.getUserInitials();
  }

  getIconState(routerLink: string): IconState {
    if (this._router.url.includes(routerLink)) {
      return IconState.Filled;
    }
    return IconState.Filled;
  }

  isActiveRoute(routerLink: string): boolean {
    return this._router.url.includes(routerLink);
  }

  goToLandRegistration() {
    this.landsPopover.hide();
    this._tutorialService.hideLandsPopover();
    this.isLandsPopoverVisible = false;

    // Mark tutorial dialog as shown in localStorage so it doesn't show again
    // This should only happen when user actually clicks the "Add Land" button
    this._localStorageService.setItem('land_tutorial_dialog_shown', true);

    this._router.navigate(['/lands/add']);
  }

  onOverlayClick(event: Event) {
    // Prevent any interaction with the overlay - do nothing
    event.preventDefault();
    event.stopPropagation();
  }

  preventEscapeClose(event: any) {
    event.preventDefault();
    event.stopPropagation();
  }

  navigateToAccount() {
    this._plantationService.showSelectPlantationPanel(false);
    if (!this.isLandsPopoverVisible) {
      this._router.navigateByUrl('/account');
    }
  }

  get unreadCount(): number {
    return this._notificationService.getUnreadCount();
  }

  get notificationBadgeValue(): string {
    const count = this.unreadCount;
    return count > 99 ? '99+' : `${count}`;
  }
}
